<template>
  <DialogComponent
    ref="searchDialog"
    type="bottomsheet"
    v-slot="{ dialogVisible }"
    @closed="onClosed"
  >
    <div class="pb-8 ">

      <!-- Header -->
      <div class="flex items-center justify-between mb-6 px-2">
        <h2 class="text-xl font-bold text-gray-900">Recherche</h2>
        <button
          v-if="hasActiveSearch"
          @click="clearSearch"
          class="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium hover:bg-gray-200 transition-colors"
        >
          Effacer
        </button>
      </div>

      <!-- Date Range with Floating Vue Dropdown -->
      <div class="mb-6 px-2">
        <h3 class="font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <svg class="w-5 h-5 text-fuchsia-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
          </svg>
          Dates
        </h3>

        <!-- Custom Date Range Dropdown -->
        <VDropdown
              theme="tooltip-light-datepicker"

          :distance="8"
          placement="bottom-end"
          :auto-hide="true"
          :delay="{ show: 0, hide: 100 }"
        >
          <!-- Trigger -->
          <div
            class="w-full px-4 py-3 border-2 rounded-xl cursor-pointer transition-all duration-200 flex items-center justify-between"
            :class="hasDateRange
              ? 'border-fuchsia-700 bg-fuchsia-50 text-fuchsia-800'
              : 'border-gray-300 text-gray-700 hover:border-fuchsia-500'"
          >
            <span class="text-sm">
              {{ dateRangeLabel }}
            </span>
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
            </svg>
          </div>

          <!-- Dropdown Content with Date Picker -->
          <template #popper="{ hide }">
            <div class="bg-white border border-gray-200 rounded-xl shadow-xl p-2">
              <DatePickerInline
                v-model="localSearch.dateRange"
                :isRange="true"
                :multiCalendars="true"
                :minDate="new Date()"
              />
              <div class="flex gap-2 p-2 border-t border-gray-100 mt-2">
                <button
                  @click="clearDateRange"
                  class="flex-1 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Effacer
                </button>
                <button
                  @click="applyDateRange(hide)"
                  class="flex-1 py-2 text-sm text-white bg-fuchsia-700 rounded-lg hover:bg-fuchsia-800"
                >
                  Confirmer
                </button>
              </div>
            </div>
          </template>
        </VDropdown>

        <!-- Quick date range buttons -->
        <div class="flex flex-wrap gap-2 mt-3">
          <button
            @click="setDateRange('today')"
            class="px-3 py-1.5 border rounded-full text-sm transition-colors"
            :class="isDateRangeActive('today')
              ? 'border-fuchsia-700 bg-fuchsia-50 text-fuchsia-800'
              : 'border-gray-300 text-gray-700 hover:border-fuchsia-500 hover:text-fuchsia-800'"
          >
            Aujourd'hui
          </button>
          <button
            @click="setDateRange('week')"
            class="px-3 py-1.5 border rounded-full text-sm transition-colors"
            :class="isDateRangeActive('week')
              ? 'border-fuchsia-700 bg-fuchsia-50 text-fuchsia-800'
              : 'border-gray-300 text-gray-700 hover:border-fuchsia-500 hover:text-fuchsia-800'"
          >
            Cette semaine
          </button>
          <button
            @click="setDateRange('month')"
            class="px-3 py-1.5 border rounded-full text-sm transition-colors"
            :class="isDateRangeActive('month')
              ? 'border-fuchsia-700 bg-fuchsia-50 text-fuchsia-800'
              : 'border-gray-300 text-gray-700 hover:border-fuchsia-500 hover:text-fuchsia-800'"
          >
            Ce mois
          </button>
          <button
            @click="setDateRange('weekend')"
            class="px-3 py-1.5 border rounded-full text-sm transition-colors"
            :class="isDateRangeActive('weekend')
              ? 'border-fuchsia-700 bg-fuchsia-50 text-fuchsia-800'
              : 'border-gray-300 text-gray-700 hover:border-fuchsia-500 hover:text-fuchsia-800'"
          >
            Ce week-end
          </button>
        </div>
      </div>

      <!-- Country -->
      <div class="mb-6 px-2">
        <h3 class="font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <svg class="w-5 h-5 text-fuchsia-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
          </svg>
          Pays
        </h3>
        <div class="relative">
          <select
            v-model="localSearch.country"
            class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-fuchsia-600 focus:border-fuchsia-600 outline-none appearance-none bg-white text-gray-700"
            :class="localSearch.country ? 'border-fuchsia-700 bg-fuchsia-50 text-fuchsia-800' : ''"
          >
            <option value="">Tous les pays</option>
            <option v-for="country in countries" :key="country.code" :value="country.code">
              {{ country.name }}
            </option>
          </select>
          <svg class="w-5 h-5 absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
        <!-- Quick country buttons -->
        <div class="flex flex-wrap gap-2 mt-3">
          <button
            v-for="country in countries"
            :key="country.code"
            @click="localSearch.country = country.code"
            class="px-3 py-1.5 border rounded-full text-sm transition-colors"
            :class="localSearch.country === country.code
              ? 'border-fuchsia-700 bg-fuchsia-50 text-fuchsia-800'
              : 'border-gray-300 text-gray-700 hover:border-fuchsia-500 hover:text-fuchsia-800'"
          >
            {{ country.name }}
          </button>
        </div>
      </div>

      <!-- Category Quick Select -->
      <div class="mb-8 px-2">
        <h3 class="font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <svg class="w-5 h-5 text-fuchsia-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"/>
          </svg>
          Catégorie
        </h3>
        <div class="grid grid-cols-3 gap-2">
          <button
            v-for="category in quickCategories"
            :key="category.id"
            @click="selectCategory(category.id)"
            class="flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all duration-200"
            :class="localSearch.category === category.id
              ? 'border-fuchsia-700 bg-fuchsia-50'
              : 'border-gray-200 hover:border-gray-300'"
          >
            <span class="text-2xl">{{ category.icon }}</span>
            <span class="text-xs font-medium">{{ category.label }}</span>
          </button>
        </div>
      </div>

      <!-- Search Button -->
      <div class="px-2">
        <button
          @click="applySearch"
          class="w-full py-4 bg-gradient-to-r from-fuchsia-800 to-fuchsia-900 text-white font-semibold rounded-2xl shadow-lg hover:shadow-xl hover:from-fuchsia-900 hover:to-purple-900 transition-all duration-200 flex items-center justify-center gap-2"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
          </svg>
          Rechercher
        </button>
      </div>
    </div>
  </DialogComponent>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { Dropdown as VDropdown } from 'floating-vue';
import 'floating-vue/dist/style.css';
import DatePickerInline from '@ui/core/input/DatePickerInline.global.vue';

const props = defineProps({
  search: {
    type: Object,
    default: () => ({})
  },
  categories: {
    type: Array,
    default: () => []
  }
});

const emit = defineEmits(['apply', 'closed']);

const searchDialog = ref(null);

// West African countries
const countries = [
  { code: 'BJ', name: 'Bénin' },
  { code: 'TG', name: 'Togo' },
  { code: 'BF', name: 'Burkina Faso' },
  { code: 'CI', name: 'Côte d\'Ivoire' },
  { code: 'SN', name: 'Sénégal' },
  { code: 'ML', name: 'Mali' }
];

const quickCategories = [
  { id: 'concerts', label: 'Concerts', icon: '🎵' },
  { id: 'festivals', label: 'Festivals', icon: '🎪' },
  { id: 'sport', label: 'Sport', icon: '⚽' },
  { id: 'theatre', label: 'Théâtre', icon: '🎭' },
  { id: 'humour', label: 'Humour', icon: '😂' },
  { id: 'all', label: 'Tous', icon: '🎭' },
];

// Local search state (copie des props)
const localSearch = ref({
  country: '',
  dateRange: [],
  category: 'all'
});

// Watch for external search changes
watch(() => props.search, (newSearch) => {
  if (newSearch) {
    localSearch.value = { ...newSearch };
  }
}, { immediate: true, deep: true });

const hasActiveSearch = computed(() => {
  return localSearch.value.country ||
         (localSearch.value.dateRange && localSearch.value.dateRange.length === 2) ||
         localSearch.value.category !== 'all';
});

const hasDateRange = computed(() => {
  return localSearch.value.dateRange && localSearch.value.dateRange.length === 2;
});

const dateRangeLabel = computed(() => {
  if (!localSearch.value.dateRange || localSearch.value.dateRange.length !== 2) {
    return 'Sélectionnez des dates';
  }
  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };
  return `${formatDate(localSearch.value.dateRange[0])} - ${formatDate(localSearch.value.dateRange[1])}`;
});

const setDateRange = (preset) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const end = new Date(today);

  switch (preset) {
    case 'today':
      localSearch.value.dateRange = [new Date(today), new Date(today)];
      break;
    case 'week':
      end.setDate(end.getDate() + 7);
      localSearch.value.dateRange = [new Date(today), new Date(end)];
      break;
    case 'month':
      end.setDate(end.getDate() + 30);
      localSearch.value.dateRange = [new Date(today), new Date(end)];
      break;
    case 'weekend':
      const dayOfWeek = today.getDay();
      const daysUntilSaturday = 6 - dayOfWeek;
      const saturday = new Date(today);
      saturday.setDate(today.getDate() + daysUntilSaturday);
      const sunday = new Date(saturday);
      sunday.setDate(saturday.getDate() + 1);
      localSearch.value.dateRange = [saturday, sunday];
      break;
  }
};

const isDateRangeActive = (preset) => {
  if (!localSearch.value.dateRange || localSearch.value.dateRange.length !== 2) {
    return false;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const start = new Date(localSearch.value.dateRange[0]);
  start.setHours(0, 0, 0, 0);
  const end = new Date(localSearch.value.dateRange[1]);
  end.setHours(0, 0, 0, 0);

  switch (preset) {
    case 'today':
      return start.getTime() === today.getTime() && end.getTime() === today.getTime();
    case 'week':
      const weekEnd = new Date(today);
      weekEnd.setDate(weekEnd.getDate() + 7);
      return start.getTime() === today.getTime() && end.getTime() === weekEnd.getTime();
    case 'month':
      const monthEnd = new Date(today);
      monthEnd.setDate(monthEnd.getDate() + 30);
      return start.getTime() === today.getTime() && end.getTime() === monthEnd.getTime();
    case 'weekend':
      const dayOfWeek = today.getDay();
      const daysUntilSaturday = 6 - dayOfWeek;
      const saturday = new Date(today);
      saturday.setDate(today.getDate() + daysUntilSaturday);
      const sunday = new Date(saturday);
      sunday.setDate(saturday.getDate() + 1);
      return start.getTime() === saturday.getTime() && end.getTime() === sunday.getTime();
    default:
      return false;
  }
};

const clearDateRange = () => {
  localSearch.value.dateRange = [];
};

const applyDateRange = (hide) => {
  hide();
};

const selectCategory = (categoryId) => {
  localSearch.value.category = categoryId;
};

const clearSearch = () => {
  localSearch.value = {
    country: '',
    dateRange: [],
    category: 'all'
  };
};

const applySearch = () => {
  emit('apply', { ...localSearch.value });
  searchDialog.value?.hide();
};

const onClosed = () => {
  emit('closed');
};

const show = () => {
  searchDialog.value?.show();
};

const hide = () => {
  searchDialog.value?.hide();
};

defineExpose({ show, hide });
</script>
